/**
 * DataValue Component - Phase 2C-1
 * 
 * Monospace number display for data clarity.
 * All numeric values in North Star should use this component.
 * 
 * Design Principle: Tabular numbers for easy scanning and comparison.
 */

export type DataValueFormat = 'number' | 'currency' | 'percentage' | 'decimal' | 'compact';
export type DataValueSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

export interface DataValueProps {
  value: number | string | null | undefined;
  format?: DataValueFormat;
  size?: DataValueSize;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  placeholder?: string;
  className?: string;
  colored?: boolean; // BSL: disabled by default, only for admin analytics
}

const sizeStyles: Record<DataValueSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
};

/**
 * Format a number based on the specified format type
 */
function formatValue(
  value: number,
  format: DataValueFormat,
  decimals?: number
): string {
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimals ?? 0,
        maximumFractionDigits: decimals ?? 0,
      }).format(value);
    
    case 'percentage':
      return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: decimals ?? 1,
        maximumFractionDigits: decimals ?? 1,
      }).format(value / 100);
    
    case 'decimal':
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals ?? 2,
        maximumFractionDigits: decimals ?? 2,
      }).format(value);
    
    case 'compact':
      return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        compactDisplay: 'short',
      }).format(value);
    
    case 'number':
    default:
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals ?? 0,
        maximumFractionDigits: decimals ?? 0,
      }).format(value);
  }
}

export function DataValue({
  value,
  format = 'number',
  size = 'md',
  decimals,
  prefix = '',
  suffix = '',
  placeholder = '—',
  className = '',
  colored = false,
}: DataValueProps): JSX.Element {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return (
      <span className={`font-mono text-slate-500 ${sizeStyles[size]} ${className}`}>
        {placeholder}
      </span>
    );
  }
  
  // Handle string values (pass through)
  if (typeof value === 'string') {
    return (
      <span
        className={`
          font-mono font-medium tabular-nums tracking-tight
          text-slate-200
          ${sizeStyles[size]}
          ${className}
        `.trim()}
      >
        {prefix}{value}{suffix}
      </span>
    );
  }
  
  // Format numeric value
  const formattedValue = formatValue(value, format, decimals);
  
  // Determine color (BSL: only for admin analytics with explicit opt-in)
  let colorClass = 'text-slate-200';
  if (colored && typeof value === 'number') {
    // BSL Compliance: Using neutral blue/gray instead of red/green
    if (value > 0) colorClass = 'text-blue-400';
    else if (value < 0) colorClass = 'text-slate-400';
  }
  
  return (
    <span
      className={`
        font-mono font-medium tabular-nums tracking-tight
        ${colorClass}
        ${sizeStyles[size]}
        ${className}
      `.trim()}
    >
      {prefix}{formattedValue}{suffix}
    </span>
  );
}

/**
 * Currency - Convenience component for currency values
 */
export interface CurrencyProps {
  value: number | null | undefined;
  size?: DataValueSize;
  decimals?: number;
  className?: string;
}

export function Currency({ value, size = 'md', decimals = 0, className = '' }: CurrencyProps): JSX.Element {
  return (
    <DataValue
      value={value}
      format="currency"
      size={size}
      decimals={decimals}
      className={className}
    />
  );
}

/**
 * Percentage - Convenience component for percentage values
 */
export interface PercentageProps {
  value: number | null | undefined;
  size?: DataValueSize;
  decimals?: number;
  className?: string;
}

export function Percentage({ value, size = 'md', decimals = 1, className = '' }: PercentageProps): JSX.Element {
  return (
    <DataValue
      value={value}
      format="percentage"
      size={size}
      decimals={decimals}
      className={className}
    />
  );
}

/**
 * Change - Display value change with +/- prefix
 * BSL: Uses neutral colors (blue/gray) instead of green/red
 */
export interface ChangeProps {
  value: number | null | undefined;
  format?: 'number' | 'currency' | 'percentage';
  size?: DataValueSize;
  decimals?: number;
  className?: string;
  showSign?: boolean;
}

export function Change({
  value,
  format = 'number',
  size = 'md',
  decimals,
  className = '',
  showSign = true,
}: ChangeProps): JSX.Element {
  if (value === null || value === undefined) {
    return <DataValue value={null} size={size} className={className} />;
  }
  
  const prefix = showSign && value > 0 ? '+' : '';
  
  return (
    <DataValue
      value={value}
      format={format}
      size={size}
      decimals={decimals}
      prefix={prefix}
      className={className}
      colored={true}
    />
  );
}

/**
 * Balance - Display balance with units suffix
 */
export interface BalanceProps {
  value: number | null | undefined;
  size?: DataValueSize;
  showUnits?: boolean;
  className?: string;
}

export function Balance({
  value,
  size = 'md',
  showUnits = true,
  className = '',
}: BalanceProps): JSX.Element {
  return (
    <DataValue
      value={value}
      format="number"
      size={size}
      suffix={showUnits ? ' units' : ''}
      className={className}
    />
  );
}

export default DataValue;
